import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  AgentTool, PromptTemplate, useListPromptTemplatesQuery, useListToolsQuery,
} from '@/redux/internal-services/agentsApi';
import { AgentFormValues } from './agentShared';
import { Button } from '@/components/UI/Button';

type Block = NonNullable<AgentFormValues['system_prompts']>[number];

/**
 * Loads a catalogue template into the agent being edited: its prompt block(s) go into the prompt list (an existing block for the same
 * module / card / sub-card is replaced, never duplicated) and its tools are enabled on the agent and placed where the template says
 * (module and card tools in the placement, sub-card tools on the block). Used by the Load template buttons.
 */
export const useLoadTemplate = () => {
  const { getValues, setValue } = useFormContext<AgentFormValues>();
  const { data: allTools } = useListToolsQuery();
  const toolCatalogue = useMemo(() => {
    const m = new Map<string, AgentTool>();
    (allTools || []).forEach((t) => { if (t.name) m.set(t.name, t); });
    return m;
  }, [allTools]);

  return (t: PromptTemplate): { blocks: number; tools: number; missing: string[] } => {
    const prompts: Block[] = [...(getValues('system_prompts') ?? [])];
    const matches = (b: Block, scope: PromptTemplate['scope'], kind: string): boolean => {
      if (scope === 'module') {
        return kind === 'general' ? b.home_module === t.key : (b.topic_modules ?? []).includes(t.key) && !b.home_module;
      }
      return (b.topic_cards ?? []).includes(t.key) && !b.home_module;
    };
    const upsert = (kind: string, block: Block) => {
      const at = prompts.findIndex((b) => matches(b, t.scope, kind));
      if (at >= 0) prompts[at] = { ...prompts[at], ...block, id: prompts[at].id };
      else prompts.push({ id: `tpl_${Math.random().toString(36).slice(2, 10)}`, ...block });
    };

    t.blocks.forEach((tb) => {
      if (t.scope === 'module') {
        upsert(tb.kind, { title: tb.title, prompt: tb.prompt, ...(tb.kind === 'general' ? { home_module: t.key } : { topic_modules: [t.key] }) });
      } else if (t.scope === 'card') {
        upsert(tb.kind, { title: tb.title, prompt: tb.prompt, topic_cards: [t.key] });
      } else {
        upsert(tb.kind, { title: tb.title, prompt: tb.prompt, topic_cards: [t.key], applies_when: tb.applies_when ?? '', tools: t.tools });
      }
    });
    setValue('system_prompts', prompts, { shouldDirty: true });

    // Enable the tools on the agent, then place them where the template says.
    const owned = [...(getValues('tools') ?? [])];
    const ownedNames = new Set(owned.map((x) => x.name || x.tool_name));
    const missing: string[] = [];
    let enabled = 0;
    t.tools.forEach((name) => {
      const tool = toolCatalogue.get(name);
      if (!tool) { missing.push(name); return; }
      if (ownedNames.has(name)) return;
      owned.push({
        tool_id: tool.tool_id, name: tool.name, tool_name: tool.tool_name || tool.name, description: tool.description,
        requires_confirmation: tool.requires_confirmation ?? false, argument_schema: tool.argument_schema,
        toolkit_type: tool.toolkit_type, toolkit_name: tool.toolkit_name, action_card_template: tool.action_card_template,
      });
      ownedNames.add(name);
      enabled += 1;
    });
    setValue('tools', owned, { shouldDirty: true });

    if (t.scope !== 'subcard') {
      const ov = getValues('tool_scope_overrides') ?? { global_tools: [], screens: {} };
      const placeable = t.tools.filter((n) => !missing.includes(n));
      const merged = [...new Set([...(ov.screens?.[t.key] ?? []), ...placeable])].sort();
      setValue('tool_scope_overrides', { ...ov, screens: { ...ov.screens, [t.key]: merged } }, { shouldDirty: true });
    }
    return { blocks: t.blocks.length, tools: enabled, missing };
  };
};

interface Props {
  scope: PromptTemplate['scope'];
  /** Module slug, card id, or `card#sub-card`. */
  templateKey: string;
  className?: string;
}

/** "Load template" for one module / card / sub-card; renders nothing when the catalogue has no template for it. */
const LoadTemplateButton: React.FC<Props> = ({ scope, templateKey, className }) => {
  const { data: templates } = useListPromptTemplatesQuery();
  const load = useLoadTemplate();
  const [done, setDone] = useState<string | null>(null);
  const template = useMemo(() => (templates ?? []).find((t) => t.scope === scope && t.key === templateKey), [templates, scope, templateKey]);
  if (!template) return null;

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = load(template);
    const bits = [`${r.blocks} block${r.blocks === 1 ? '' : 's'} loaded`, `${r.tools} tool${r.tools === 1 ? '' : 's'} enabled`];
    setDone(bits.join(' · ') + (r.missing.length ? ` · ${r.missing.length} not in the tool catalogue` : ''));
    window.setTimeout(() => setDone(null), 5000);
  };

  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ''}`}>
      <Button
        type="button" variant="soft" size="xs" onClick={onClick}
        title={`${template.title}: ${template.description}`}
        aria-label={`Load template for ${templateKey}`}
      >
        Load template
      </Button>
      {done && <span role="status" className="text-xs text-success-text">✓ {done}</span>}
    </span>
  );
};

export default LoadTemplateButton;
